import { safeRandomUUID } from './utils';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface DebugConfig {
  enabled: boolean;
  logLevel: LogLevel;
  showDebugPanel: boolean;
  logApiRequests: boolean;
  logComponentLifecycle: boolean;
  logStateChanges: boolean;
  logPerformance: boolean;
  maxLogEntries: number;
  persistLogs: boolean;
}

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  category: 'api' | 'component' | 'state' | 'error' | 'performance' | 'general';
  message: string;
  data?: any;
  stackTrace?: string;
  componentName?: string;
  apiEndpoint?: string;
  duration?: number;
}

class DebugConfigManager {
  private config: DebugConfig;
  private logs: LogEntry[] = [];
  private listeners: Set<(logs: LogEntry[]) => void> = new Set();
  private storageKey = 'yapzap_debug_config';
  private logsKey = 'yapzap_debug_logs';

  constructor() {
    this.config = this.loadConfig();
    if (this.config.persistLogs) {
      this.logs = this.loadLogs();
    }
    this.checkUrlParams();
  }

  private getDefaultConfig(): DebugConfig {
    return {
      enabled: import.meta.env.DEV || false,
      logLevel: 'info',
      showDebugPanel: false,
      logApiRequests: true,
      logComponentLifecycle: false,
      logStateChanges: false,
      logPerformance: true,
      maxLogEntries: 1000,
      persistLogs: true,
    };
  }

  private loadConfig(): DebugConfig {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return { ...this.getDefaultConfig(), ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Failed to load debug config:', error);
    }
    return this.getDefaultConfig();
  }

  private saveConfig(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.config));
    } catch (error) {
      console.error('Failed to save debug config:', error);
    }
  }

  private loadLogs(): LogEntry[] {
    try {
      const stored = localStorage.getItem(this.logsKey);
      if (stored) {
        const logs = JSON.parse(stored);
        return logs.map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp),
        }));
      }
    } catch (error) {
      console.error('Failed to load debug logs:', error);
    }
    return [];
  }

  private saveLogs(): void {
    if (!this.config.persistLogs) return;

    try {
      localStorage.setItem(this.logsKey, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Failed to save debug logs:', error);
    }
  }

  private checkUrlParams(): void {
    const params = new URLSearchParams(window.location.search);

    if (params.has('debug')) {
      this.config.enabled = params.get('debug') === 'true';
      this.config.showDebugPanel = true;
      this.saveConfig();
    }

    if (params.has('debugLevel')) {
      const level = params.get('debugLevel') as LogLevel;
      if (['debug', 'info', 'warn', 'error'].includes(level)) {
        this.config.logLevel = level;
        this.saveConfig();
      }
    }
  }

  getConfig(): DebugConfig {
    return { ...this.config };
  }

  updateConfig(updates: Partial<DebugConfig>): void {
    this.config = { ...this.config, ...updates };
    this.saveConfig();
  }

  isEnabled(): boolean {
    return this.config.enabled;
  }

  shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;

    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.config.logLevel);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex >= currentLevelIndex;
  }

  addLog(entry: Omit<LogEntry, 'id' | 'timestamp'>): void {
    if (!this.shouldLog(entry.level)) return;

    const newEntry: LogEntry = {
      ...entry,
      id: safeRandomUUID(),
      timestamp: new Date(),
    };

    this.logs.unshift(newEntry);

    if (this.logs.length > this.config.maxLogEntries) {
      this.logs = this.logs.slice(0, this.config.maxLogEntries);
    }

    this.saveLogs();
    this.notifyListeners();
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
    localStorage.removeItem(this.logsKey);
    this.notifyListeners();
  }

  subscribe(listener: (logs: LogEntry[]) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.logs));
  }

  exportLogs(): string {
    const exportData = {
      config: this.config,
      logs: this.logs,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
    return JSON.stringify(exportData, null, 2);
  }

  downloadLogs(): void {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yapzap-debug-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const debugConfig = new DebugConfigManager();

export const log = {
  debug: (message: string, data?: any) => {
    debugConfig.addLog({ level: 'debug', category: 'general', message, data });
  },
  info: (message: string, data?: any) => {
    debugConfig.addLog({ level: 'info', category: 'general', message, data });
  },
  warn: (message: string, data?: any) => {
    debugConfig.addLog({ level: 'warn', category: 'general', message, data });
  },
  error: (message: string, error?: any) => {
    const stackTrace = error?.stack || new Error().stack;
    debugConfig.addLog({
      level: 'error',
      category: 'error',
      message,
      data: error,
      stackTrace,
    });
  },
};
